import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AppDispatch, RootState } from '../../src/redux/store';
import {
  chooseDirection,
  selectOnboardingAnswers,
  selectOnboardingCompleted,
  selectOnboardingPhase,
  selectOnboardingTonight,
  selectOnboardingWeek,
  type OnboardingDirection,
} from '../../src/redux/onboardingSlice';
import type { OnboardingIdea } from '../../src/services/onboardingWeek';
import { finishOnboarding } from '../../src/services/onboardingFinish';
import { OnboardingFrame, ONBOARDING_DESKTOP_MIN } from '../../src/components/onboarding/OnboardingFrame';
import { DirectionCard } from '../../src/components/onboarding/DirectionCard';
import { DuChefBubble } from '../../src/components/onboarding/DuChefBubble';
import { parseIsoDate } from '../../src/utils/localDate';
import { Paprika } from '../../src/styles/theme';

/**
 * Route /onboarding/direction — « Que voulez-vous faire maintenant ? ». Both cards
 * run the same finish ritual and then replace the route with a screen that
 * already exists: the week planner or the recipes screen. A recipe tapped on
 * the ideas card opens on top of the recipes screen, so back lands there.
 */
export default function OnboardingDirectionScreen() {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const { t, i18n } = useTranslation();
  const { width } = useWindowDimensions();
  const desktop = width >= ONBOARDING_DESKTOP_MIN;

  const completed = useSelector(selectOnboardingCompleted);
  const answers = useSelector(selectOnboardingAnswers);
  const phase = useSelector(selectOnboardingPhase);
  const week = useSelector(selectOnboardingWeek);
  const tonight = useSelector(selectOnboardingTonight);
  // A ref, not state: `completeOnboarding` re-renders this screen through the
  // store before a setState from the same handler would, and the effect below
  // must not see `completed` first and send the cook to the week screen.
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    if (completed) router.replace('/(app)');
    else if (answers.length < 3) router.replace('/onboarding');
    // The week is built on the profile screen; without it there is nothing to preview.
    else if (phase !== 'direction') router.replace('/onboarding/profile');
  }, [completed, answers.length, phase, router]);

  const dayLabel = useCallback((iso: string) => {
    const d = parseIsoDate(iso);
    return new Intl.DateTimeFormat(i18n.language?.startsWith('fr') ? 'fr-CA' : 'en-CA', { weekday: 'long' }).format(d);
  }, [i18n.language]);

  const choose = useCallback((mode: OnboardingDirection) => {
    if (done.current) return;
    done.current = true;
    dispatch(chooseDirection(mode));
    const { route } = finishOnboarding(dispatch, () => store.getState(), mode);
    router.replace(route);
  }, [dispatch, store, router]);

  // #721: the card promised this recipe, so open it rather than the library.
  const pickIdea = useCallback((idea: OnboardingIdea) => {
    if (done.current) return;
    done.current = true;
    dispatch(chooseDirection('ideas', 'idea'));
    const { route } = finishOnboarding(dispatch, () => store.getState(), 'ideas');
    router.replace(route);
    router.push({
      pathname: `/(app)/recipe-detail/${idea.id}`,
      params: {
        recipeData: JSON.stringify({
          id: idea.id,
          title: idea.title,
          image: idea.image,
          readyInMinutes: idea.readyInMinutes,
        }),
        // Back from the recipe goes to the recipes screen, not to onboarding.
        from: 'onboarding',
      },
    });
  }, [dispatch, store, router]);

  const title = (
    <Text style={styles.title}>
      {t('onboarding.direction.title')}{' '}
      <Text style={styles.titleEm}>{t('onboarding.direction.titleEmphasis')}</Text>
      {t('onboarding.direction.titleEnd')}
    </Text>
  );

  // Desktop: the question sits right above the two cards so the buttons read
  // as its answers; the rail keeps Du Chef's line. Phone: title over cards.
  const bubble = <DuChefBubble tone="say" testID="onb-direction-bubble">{t('onboarding.direction.bubble')}</DuChefBubble>;

  return (
    <OnboardingFrame kicker={t('onboarding.direction.kicker')} rail={desktop ? bubble : title}>
      <View style={desktop ? styles.center : styles.fill}>
        {desktop && <View style={styles.deskTitle}>{title}</View>}
        <View style={[styles.cards, desktop ? styles.cardsSide : styles.cardsStack]}>
          <DirectionCard kind="plan" meals={week} dayLabel={dayLabel} onPress={() => choose('plan')} />
          <DirectionCard kind="ideas" ideas={tonight} onPress={() => choose('ideas')} onPickIdea={pickIdea} />
        </View>
      </View>
    </OnboardingFrame>
  );
}

const styles = StyleSheet.create({
  title: { fontFamily: 'Newsreader-Regular', fontSize: 28, lineHeight: 34, color: Paprika.textPrimary },
  titleEm: { fontFamily: 'Newsreader-Italic', color: Paprika.accent },
  fill: { flex: 1, minHeight: 0 },
  // Desktop: two shorter cards, centred in the pane rather than stretched to
  // its floor (decision 2026-09-17).
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 22 },
  deskTitle: { width: '100%', maxWidth: 940 },
  // No `flex` shorthand on the desktop row: RNW turns `flex: 0` into
  // `flex-basis: 0%`, which beats an explicit height in a column parent.
  cards: { gap: 14, minHeight: 0 },
  cardsStack: { flex: 1, flexDirection: 'column' },
  cardsSide: { flexDirection: 'row', gap: 16, width: '100%', maxWidth: 940, height: 420 },
});
